import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useCachedGet } from "@/lib/cache";

export const PAGE_SIZE = 24;

// Lazy-loaded ads: first page cached, next pages appended as the user scrolls (24 per load).
export function usePagedListings(params, opts) {
  const first = useCachedGet("/listings", { ...params, limit: PAGE_SIZE }, opts);
  const key = JSON.stringify(params);
  const [more, setMore] = useState({ key, items: [], hasMore: null, busy: false });
  const cur = more.key === key ? more : { key, items: [], hasMore: null, busy: false };
  const firstItems = first.data?.items;
  useEffect(() => { setMore({ key, items: [], hasMore: null, busy: false }); }, [key, firstItems]);
  const items = [...(first.data?.items || []), ...cur.items];
  const hasMore = cur.hasMore ?? !!first.data?.has_more;
  const loadMore = useCallback(async () => {
    if (!hasMore || cur.busy || !first.data) return;
    setMore({ ...cur, busy: true });
    try {
      const { data } = await api.get("/listings", { params: { ...params, limit: PAGE_SIZE, skip: items.length } });
      setMore((m) => (m.key === key ? { key, items: [...m.items, ...data.items], hasMore: data.has_more, busy: false } : m));
    } catch { setMore((m) => ({ ...m, busy: false })); }
  }, [hasMore, cur, first.data, items.length, key]); // eslint-disable-line react-hooks/exhaustive-deps
  return { items, total: first.data?.total ?? 0, loading: first.loading, hasMore, busy: cur.busy, loadMore };
}

export function LoadMoreSentinel({ hasMore, busy, onMore, count }) {
  const ref = useRef(null);
  const cb = useRef(onMore);
  cb.current = onMore;
  useEffect(() => {
    if (!hasMore || !ref.current) return undefined;
    const io = new IntersectionObserver((e) => { if (e[0].isIntersecting) cb.current(); }, { rootMargin: "600px" });
    io.observe(ref.current);
    return () => io.disconnect();
  }, [hasMore, count, busy]);
  if (!hasMore) return null;
  return <div ref={ref} className="flex justify-center py-6" data-testid="load-more-sentinel">{busy && <Loader2 className="w-6 h-6 animate-spin text-[#1A5C3A]" />}</div>;
}
